import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useSearchParams } from "react-router-dom";
import { AdsPageViews } from "./components/AdsPageViews";
import "@/App.css";
import { I18nProvider, useI18n } from "./i18n";
import { ErrorBoundary } from "./components/common/ErrorBoundary";
import { IosInstallHint, OfflineScreen, UpdateBanner } from "./components/pwa/PwaUi";
import { NetworkRetry } from "./components/common/NetworkRetry";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { PublicSite, ReferralLanding, RootRedirect } from "./public/PublicSite";

const named = (load, name) => lazy(() => load().then((m) => ({ default: m[name] })));
const Toaster = named(() => import("./components/ui/sonner"), "Toaster");
const AppShell = named(() => import("./components/shell/AppShell"), "AppShell");
// Auth screens can be preloaded before mount (index.js) so they replace the pre-painted HTML without a loading flash.
const authMods = {};
export const loadAuthPages = () => Promise.all([import("./pages/auth/LoginSignup"), import("./pages/auth/PasswordReset")]).then((ms) => Object.assign(authMods, ...ms));
const authPage = (name, load) => {
  const Lazy = named(load, name);
  return (props) => { const C = authMods[name] || Lazy; return <C {...props} />; };
};
const Login = authPage("Login", () => import("./pages/auth/LoginSignup"));
const Signup = authPage("Signup", () => import("./pages/auth/LoginSignup"));
const ForgotPassword = authPage("ForgotPassword", () => import("./pages/auth/PasswordReset"));
const ResetPassword = named(() => import("./pages/auth/PasswordReset"), "ResetPassword");
const InvitePage = lazy(() => import("./pages/auth/InvitePage"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const QuickAudit = lazy(() => import("./pages/QuickAudit"));
const Leads = lazy(() => import("./pages/Leads"));
const Campaigns = lazy(() => import("./pages/Campaigns"));
const CampaignNew = lazy(() => import("./pages/CampaignNew"));
const CampaignDetail = lazy(() => import("./pages/CampaignDetail"));
const ProspectPage = lazy(() => import("./pages/ProspectPage"));
const Credits = lazy(() => import("./pages/Credits"));
const Team = lazy(() => import("./pages/Team"));
const Settings = lazy(() => import("./pages/Settings"));
const Mailboxes = lazy(() => import("./pages/Mailboxes"));
const Inbox = lazy(() => import("./pages/Inbox"));
const ReviewRedirect = named(() => import("./pages/Campaigns"), "ReviewRedirect");
const Billing = lazy(() => import("./pages/Billing"));
const Affiliates = lazy(() => import("./pages/Affiliates"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const Pipeline = lazy(() => import("./pages/Pipeline"));
const LeadFinder = lazy(() => import("./pages/LeadFinder"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const EmailPreferences = lazy(() => import("./pages/EmailPreferences"));

const Splash = () => {
  const { t } = useI18n();
  return <div className="am-splash" data-testid="app-loading">{t("common.loading")}</div>;
};

// The exchange itself runs once in AuthProvider (before any /auth/me); this only waits for its result.
const AuthCallback = () => {
  const { me, googleError } = useAuth();
  if (me) return <Navigate to="/dashboard" replace />;
  if (me === false) return <Navigate to={`/login?google=failed&reason=${encodeURIComponent(googleError || "no_session")}`} replace />;
  return <Splash />;
};

const Protected = ({ children }) => {
  const { me } = useAuth();
  const location = useLocation();
  if (me === null) return <Splash />;
  if (!me) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  return <AppShell><Suspense fallback={<Splash />}>{children}</Suspense></AppShell>;
};

const PublicOnly = ({ children }) => {
  const { me } = useAuth();
  const [params] = useSearchParams();
  if (me === null) return window.__amAuthShell ? children : <Splash />;
  const plan = params.get("plan");
  if (!me) return children;
  const next = params.get("next");
  if (next && next.startsWith("/") && !next.startsWith("//")) return <Navigate to={next} replace />;
  return <Navigate to={plan ? `/billing?plan=${plan}&interval=${params.get("interval") || "monthly"}` : "/dashboard"} replace />;
};

const AppRouter = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  const p = (el) => <Protected>{el}</Protected>;
  return (
    <Suspense fallback={<Splash />}>
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/signup" element={<PublicOnly><Signup /></PublicOnly>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="/dashboard" element={p(<Dashboard />)} />
      <Route path="/quick-audit" element={p(<QuickAudit />)} />
      <Route path="/quick-audit/:id" element={p(<QuickAudit />)} />
      <Route path="/leads" element={p(<Leads />)} />
      <Route path="/campaigns" element={p(<Campaigns />)} />
      <Route path="/campaigns/new" element={p(<CampaignNew />)} />
      <Route path="/campaigns/:cid" element={p(<CampaignDetail />)} />
      <Route path="/campaigns/:cid/leads/:pid" element={p(<ProspectPage />)} />
      <Route path="/credits" element={p(<Credits />)} />
      <Route path="/team" element={p(<Team />)} />
      <Route path="/settings" element={p(<Settings />)} />
      <Route path="/mailboxes" element={p(<Mailboxes />)} />
      <Route path="/inbox" element={p(<Inbox />)} />
      <Route path="/inbox/:rid" element={p(<Inbox />)} />
      <Route path="/review" element={p(<ReviewRedirect />)} />
      <Route path="/pipeline" element={p(<Pipeline />)} />
      <Route path="/finder" element={p(<LeadFinder />)} />
      <Route path="/unsubscribe/:token" element={<Unsubscribe />} />
      <Route path="/email-preferences" element={<EmailPreferences />} />
      <Route path="/billing" element={p(<Billing />)} />
      <Route path="/affiliates" element={p(<Affiliates />)} />
      <Route path="/admin" element={p(<AdminPage />)} />
      <Route path="/r/:code" element={<ReferralLanding />} />
      <Route path="/" element={<RootRedirect />} />
      <Route path="/:lang/*" element={<PublicSite />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </Suspense>
  );
};

const ToasterWithDir = () => {
  const { rtl } = useI18n();
  return <Suspense fallback={null}><Toaster position={rtl ? "bottom-left" : "bottom-right"} dir={rtl ? "rtl" : "ltr"} mobileOffset={{ bottom: "calc(72px + env(safe-area-inset-bottom))" }} /></Suspense>;
};

export default function App() {
  return (
    <I18nProvider>
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            <AdsPageViews />
            <ToasterWithDir />
            <UpdateBanner />
            <IosInstallHint />
            <OfflineScreen />
            <NetworkRetry />
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </I18nProvider>
  );
}
